import React, { useCallback, useEffect, useRef, useState } from 'react';
import { colors } from '../theme/tokens';

// Touch-first square cropper.
//   - Auto-fits to the largest dark blob (the profile) so it fills ~90% of the frame
//   - One finger drag = pan, two finger pinch = zoom
//   - Mouse drag = pan, wheel = zoom
//   - Buttons for ↺ Reset · − · + · ✓ Use this crop
//
// Implementation notes:
//   * Down-event sits on the frame element, but move/up listeners are attached
//     to `window`. This is the most robust pattern for drag UIs — it works
//     identically across mouse and touch, never loses events when the pointer
//     leaves the frame, and doesn't need setPointerCapture.
//
// Props:
//   file:       File or Blob (the original photo from the camera or picker)
//   onConfirm:  (croppedFile: File) => void   called with a 1024×1024 JPEG
//   onCancel:   () => void                    user wants to retake
//   outputSize: number (default 1024)         pixel size of the square output
//   targetFill: number (default 0.9)          fraction of frame the auto-fit profile occupies
const OUTPUT_DEFAULT = 1024;
const TARGET_FILL_DEFAULT = 0.9;
const DOWNSCALE_FOR_DETECT = 256;
const MIN_ZOOM = 0.05;
const MAX_ZOOM = 20;

const ProfileCropper = ({ file, onConfirm, onCancel, outputSize = OUTPUT_DEFAULT, targetFill = TARGET_FILL_DEFAULT }) => {
  const wrapperRef = useRef(null);
  const imgRef = useRef(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageSize, setImageSize] = useState({ w: 0, h: 0 });
  const [frameSize, setFrameSize] = useState(0);
  const [transform, setTransform] = useState({ pan: { x: 0, y: 0 }, zoom: 1 });
  const [busy, setBusy] = useState(false);

  // We keep the latest transform in a ref so the gesture handlers attached to
  // window can read the up-to-date value without us needing to re-bind them.
  const transformRef = useRef(transform);
  useEffect(() => { transformRef.current = transform; }, [transform]);

  // Gesture state (single object kept in a ref to avoid re-renders mid-drag).
  // pointers maps id -> { x, y }
  // startPan/startZoom snapshot the transform at gesture start
  // startCentre/startDistance snapshot the two-finger geometry at gesture start
  const gestureRef = useRef({
    pointers: new Map(),
    startPan: { x: 0, y: 0 },
    startZoom: 1,
    startCentre: null,
    startDistance: 0
  });

  // 1. Load file -> object URL
  useEffect(() => {
    if (!file) { setImageUrl(null); return; }
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // 2. Once image is loaded, auto-fit
  const handleImgLoad = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    const w = img.naturalWidth, h = img.naturalHeight;
    setImageSize({ w, h });
    autoFit(img, w, h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 3. Measure the on-screen square viewport (initial + on resize)
  useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) setFrameSize(wrapperRef.current.clientWidth);
    };
    measure();
    // Re-measure shortly after mount in case layout settles
    const tid = setTimeout(measure, 50);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(tid);
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Auto-detection: downscale, threshold, find dark bbox.
  const autoFit = useCallback((img, imgW, imgH) => {
    let bbox = null;
    try { bbox = detectDarkBBox(img, imgW, imgH); } catch (e) { console.warn('autoFit detect failed', e); }
    if (!bbox) bbox = { x: 0, y: 0, w: imgW, h: imgH };

    const bboxMax = Math.max(bbox.w, bbox.h) || 1;
    const zoom = bboxMax / (outputSize * targetFill);
    const cx = bbox.x + bbox.w / 2;
    const cy = bbox.y + bbox.h / 2;
    setTransform({ pan: { x: cx, y: cy }, zoom: clampZoom(zoom) });
  }, [outputSize, targetFill]);

  const reset = () => {
    const img = imgRef.current;
    if (img && imageSize.w) autoFit(img, imageSize.w, imageSize.h);
  };

  const zoomBy = (factor) => {
    setTransform(t => ({ ...t, zoom: clampZoom(t.zoom * factor) }));
  };

  // ── Window-level gesture handling ────────────────────────────────────────
  // Started by pointerdown on the frame. While the gesture is active we
  // listen on window so the user can drag outside the frame freely.

  const handleWindowPointerMove = useCallback((e) => {
    const g = gestureRef.current;
    if (!g.pointers.has(e.pointerId)) return;
    g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (g.pointers.size === 1) {
      const only = g.pointers.values().next().value;
      const dx = only.x - g.startCentre.x;
      const dy = only.y - g.startCentre.y;
      setTransform({
        pan: {
          x: g.startPan.x - dx * g.startZoom,
          y: g.startPan.y - dy * g.startZoom
        },
        zoom: g.startZoom
      });
    } else if (g.pointers.size >= 2) {
      const pts = Array.from(g.pointers.values()).slice(0, 2);
      const curCentre = midPoint(pts[0], pts[1]);
      const curDistance = distance(pts[0], pts[1]);
      if (!g.startDistance || curDistance <= 0) return;

      const zoomFactor = g.startDistance / curDistance;
      const newZoom = clampZoom(g.startZoom * zoomFactor);

      const dxScreen = curCentre.x - g.startCentre.x;
      const dyScreen = curCentre.y - g.startCentre.y;
      setTransform({
        pan: {
          x: g.startPan.x - dxScreen * g.startZoom,
          y: g.startPan.y - dyScreen * g.startZoom
        },
        zoom: newZoom
      });
    }
  }, []);

  const handleWindowPointerUp = useCallback((e) => {
    const g = gestureRef.current;
    if (!g.pointers.has(e.pointerId)) return;
    g.pointers.delete(e.pointerId);

    if (g.pointers.size === 1) {
      // Promote the remaining finger to a fresh pan-start so panning continues smoothly
      const only = g.pointers.values().next().value;
      g.startCentre = { x: only.x, y: only.y };
      g.startPan = { ...transformRef.current.pan };
      g.startZoom = transformRef.current.zoom;
      g.startDistance = 0;
    } else if (g.pointers.size === 0) {
      detachWindowGestureListeners();
    }
    // size >= 2 — still mid-pinch, keep listeners attached
  }, [handleWindowPointerMove]); // eslint-disable-line react-hooks/exhaustive-deps

  const listenersAttachedRef = useRef(false);
  const attachWindowGestureListeners = useCallback(() => {
    if (listenersAttachedRef.current) return;
    window.addEventListener('pointermove', handleWindowPointerMove);
    window.addEventListener('pointerup', handleWindowPointerUp);
    window.addEventListener('pointercancel', handleWindowPointerUp);
    listenersAttachedRef.current = true;
  }, [handleWindowPointerMove, handleWindowPointerUp]);

  const detachWindowGestureListeners = useCallback(() => {
    if (!listenersAttachedRef.current) return;
    window.removeEventListener('pointermove', handleWindowPointerMove);
    window.removeEventListener('pointerup', handleWindowPointerUp);
    window.removeEventListener('pointercancel', handleWindowPointerUp);
    listenersAttachedRef.current = false;
    gestureRef.current.pointers.clear();
  }, [handleWindowPointerMove, handleWindowPointerUp]);

  // Detach on unmount
  useEffect(() => detachWindowGestureListeners, [detachWindowGestureListeners]);

  const onFramePointerDown = (e) => {
    // Don't preventDefault — it can cause issues on some Android browsers
    // and isn't needed because we use `touch-action: none` on the frame.
    const g = gestureRef.current;
    g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (g.pointers.size === 1) {
      g.startCentre = { x: e.clientX, y: e.clientY };
      g.startPan = { ...transformRef.current.pan };
      g.startZoom = transformRef.current.zoom;
      g.startDistance = 0;
    } else if (g.pointers.size >= 2) {
      const pts = Array.from(g.pointers.values()).slice(0, 2);
      g.startCentre = midPoint(pts[0], pts[1]);
      g.startDistance = distance(pts[0], pts[1]);
      g.startPan = { ...transformRef.current.pan };
      g.startZoom = transformRef.current.zoom;
    }
    attachWindowGestureListeners();
  };

  // Mouse-wheel zoom — non-passive listener via direct addEventListener.
  // React's onWheel can be passive in some configs (warning when calling preventDefault).
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 1.1 : (1 / 1.1);
      setTransform(t => ({ ...t, zoom: clampZoom(t.zoom * factor) }));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Image visual style
  const imgStyle = (() => {
    if (!imageSize.w || !frameSize) return { visibility: 'hidden' };
    const displayScale = (frameSize / outputSize) / Math.max(transform.zoom, 0.0001);
    const offsetX = frameSize / 2 - transform.pan.x * displayScale;
    const offsetY = frameSize / 2 - transform.pan.y * displayScale;
    return {
      position: 'absolute',
      left: offsetX + 'px',
      top: offsetY + 'px',
      width: imageSize.w * displayScale + 'px',
      height: imageSize.h * displayScale + 'px',
      pointerEvents: 'none',
      userSelect: 'none',
      maxWidth: 'none',
      maxHeight: 'none'
    };
  })();

  // ── Confirm: render the cropped square to an off-screen canvas
  const confirm = async () => {
    setBusy(true);
    try {
      const img = imgRef.current;
      if (!img) throw new Error('image element not ready');
      const canvas = document.createElement('canvas');
      canvas.width = outputSize;
      canvas.height = outputSize;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, outputSize, outputSize);

      const side = outputSize * transform.zoom;
      const sx = transform.pan.x - side / 2;
      const sy = transform.pan.y - side / 2;
      ctx.drawImage(img, sx, sy, side, side, 0, 0, outputSize, outputSize);

      const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.9));
      if (!blob) throw new Error('toBlob returned null');
      const cropped = new File(
        [blob],
        ((file && file.name) || 'photo').replace(/\.\w+$/, '') + '-crop.jpg',
        { type: 'image/jpeg' }
      );
      onConfirm && onConfirm(cropped);
    } catch (e) {
      console.error('crop failed:', e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.heading}>✂️ Crop to fill the square</div>
      <div style={styles.hint}>Drag to pan · pinch / scroll to zoom · the profile should fill the frame</div>

      <div
        ref={wrapperRef}
        style={styles.frame}
        onPointerDown={onFramePointerDown}
      >
        {imageUrl && (
          <img
            ref={imgRef}
            src={imageUrl}
            alt="to-crop"
            onLoad={handleImgLoad}
            style={imgStyle}
            draggable={false}
          />
        )}
        <div style={styles.gridOverlay}>
          <div style={styles.gridV1} /><div style={styles.gridV2} />
          <div style={styles.gridH1} /><div style={styles.gridH2} />
        </div>
        <div style={styles.frameBorder} />
      </div>

      <div style={styles.buttonRow}>
        <button type="button" style={styles.smallBtn} onClick={() => zoomBy(1 / 1.2)}>−</button>
        <button type="button" style={styles.smallBtn} onClick={() => zoomBy(1.2)}>+</button>
        <button type="button" style={styles.smallBtn} onClick={reset}>↺ Auto-fit</button>
        <div style={{ flex: 1 }} />
        <button type="button" style={styles.cancelBtn} onClick={onCancel} disabled={busy}>↺ Retake</button>
        <button type="button" style={styles.confirmBtn} onClick={confirm} disabled={busy || !imageUrl}>
          {busy ? '…' : '✓ Use this crop'}
        </button>
      </div>
    </div>
  );
};

// Cheap auto-detect: downscale, find bounding box of dark pixels.
function detectDarkBBox(img, w, h) {
  try {
    const scale = DOWNSCALE_FOR_DETECT / Math.max(w, h);
    const dw = Math.max(1, Math.round(w * scale));
    const dh = Math.max(1, Math.round(h * scale));
    const c = document.createElement('canvas');
    c.width = dw; c.height = dh;
    const ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0, dw, dh);
    const data = ctx.getImageData(0, 0, dw, dh).data;

    let sum = 0;
    const gray = new Uint8Array(dw * dh);
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
      const v = (data[i] + data[i + 1] + data[i + 2]) / 3;
      gray[p] = v;
      sum += v;
    }
    const mean = sum / gray.length;
    const threshold = Math.max(40, mean - 20);

    let minX = dw, minY = dh, maxX = -1, maxY = -1, count = 0;
    for (let y = 0; y < dh; y++) {
      for (let x = 0; x < dw; x++) {
        if (gray[y * dw + x] < threshold) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
          count++;
        }
      }
    }
    if (count < 10 || maxX < 0) return null;
    const bboxArea = (maxX - minX + 1) * (maxY - minY + 1);
    if (bboxArea > 0.9 * dw * dh) return null;

    return {
      x: minX / scale,
      y: minY / scale,
      w: (maxX - minX + 1) / scale,
      h: (maxY - minY + 1) / scale
    };
  } catch (e) {
    console.warn('detectDarkBBox failed', e);
    return null;
  }
}

function midPoint(a, b) { return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; }
function distance(a, b) { const dx = a.x - b.x, dy = a.y - b.y; return Math.sqrt(dx * dx + dy * dy); }
function clampZoom(z) { return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z)); }

const styles = {
  card: {
    background: colors.surface, borderRadius: 12, padding: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  heading: { fontSize: 16, fontWeight: 700, color: colors.text },
  hint: { fontSize: 12, color: colors.textMuted, marginBottom: 10 },
  frame: {
    position: 'relative',
    width: '100%',
    aspectRatio: '1 / 1',
    background: '#222',
    borderRadius: 10,
    overflow: 'hidden',
    touchAction: 'none',
    cursor: 'grab',
    userSelect: 'none'
  },
  frameBorder: {
    position: 'absolute', inset: 0,
    border: '2px solid rgba(47,182,92,0.85)',
    borderRadius: 10,
    pointerEvents: 'none',
    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.4)'
  },
  gridOverlay: { position: 'absolute', inset: 0, pointerEvents: 'none' },
  gridV1: { position: 'absolute', left: '33.33%', top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.25)' },
  gridV2: { position: 'absolute', left: '66.66%', top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.25)' },
  gridH1: { position: 'absolute', top: '33.33%', left: 0, right: 0, height: 1, background: 'rgba(255,255,255,0.25)' },
  gridH2: { position: 'absolute', top: '66.66%', left: 0, right: 0, height: 1, background: 'rgba(255,255,255,0.25)' },
  buttonRow: {
    display: 'flex', gap: 6, marginTop: 12, alignItems: 'center', flexWrap: 'wrap'
  },
  smallBtn: {
    padding: '8px 12px', background: colors.surface, border: `1px solid ${colors.border}`,
    borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, color: colors.text
  },
  cancelBtn: {
    padding: '10px 14px', background: colors.surface, border: `1px solid ${colors.border}`,
    borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, color: colors.text
  },
  confirmBtn: {
    padding: '10px 16px', background: '#2fb65c', color: 'white', border: 'none',
    borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14
  }
};

export default ProfileCropper;
